/**
 * useUserDashboard Hook
 * 
 * Aggregates data from multiple services for user dashboard.
 * Fetches user status, transactions, offers, and fulfillments.
 * Calculates pointsToday and lifetimeEarned from transactions.
 */

import { useState, useEffect, useCallback } from 'react';
import type { UserDashboardData } from '@/app/types/user';
import { userStatusService } from '@/app/services/userStatusService';
import { transactionService } from '@/app/services/transactionService';
import { offerService } from '@/app/services/offerService';
import { fulfillmentService } from '@/app/services/fulfillmentService';
import { logger } from '@/app/utils/logger';

export function useUserDashboard(userId: number | null) {
  const [data, setData] = useState<UserDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (!userId) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch user status
      const status = await userStatusService.getUserStatus(userId);

      // Fetch transactions filtered by community_ref_user_id
      const transactionsResponse = await transactionService.queryTransactions({
        community_ref_user_id: userId,
        limit: 100, // Get enough to calculate stats
        offset: 0,
      });

      // Fetch offers filtered by user_id (as string)
      const offersResponse = await offerService.queryOffers({
        user_id: userId.toString(),
        limit: 100,
        offset: 0,
      });

      // Fetch fulfillments filtered by user_id (as string)
      const fulfillmentsResponse = await fulfillmentService.queryFulfillments({
        user_id: userId.toString(),
        limit: 100,
        offset: 0,
      });

      // Fetch reward limits for all assigned offers
      const rewardLimits = await userStatusService.getUserRewardLimits(userId);

      // Calculate pointsToday (last 24h earns)
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const pointsToday = transactionsResponse.transactions
        .filter(tx => {
          const txDate = new Date(tx.transaction_datetime_utc);
          return txDate >= yesterday && tx.transaction_type === 'earn';
        })
        .reduce((sum, tx) => sum + tx.point_value, 0);

      // Calculate lifetimeEarned (sum of all positive point values)
      const lifetimeEarned = transactionsResponse.transactions
        .filter(tx => tx.point_value > 0)
        .reduce((sum, tx) => sum + tx.point_value, 0);

      // Get recent transactions (last 10, sorted by date desc)
      const recentTransactions = transactionsResponse.transactions
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);

      // Get active offers (all offers for now, could filter by status if needed)
      const activeOffers = offersResponse.offers;

      setData({
        status,
        pointsToday,
        lifetimeEarned,
        recentTransactions,
        activeOffers,
        fulfillments: fulfillmentsResponse.fulfillments,
        rewardLimits,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user dashboard';
      logger.error('Failed to fetch user dashboard data', { userId, error: err });
      setError(errorMessage);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Fetch data when userId changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    isLoading,
    error,
    refresh: fetchDashboardData,
  };
}




